import { Input, OnInit, HostListener, Component } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgOptimizedImage } from '@angular/common';

export interface VideoItem {
  id: string;
  title: string;
  type: 'youtube' | 'image';
  src: string;
  thumb: string;
}

@Component({
  selector: 'app-video-carousel',
  standalone: true,
  imports: [NgOptimizedImage],
  template: `
    <div class="video-carousel-container" role="region" aria-label="Video and Image Gallery">
      <div class="main-display" role="tabpanel" aria-live="polite">
        @switch (activeItem.type) {
          @case ('youtube') {
            <iframe
              [src]="getSafeUrl(activeItem.src)"
              width="100%" height="544"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              [title]="activeItem.title">
            </iframe>
          }
          @case ('image') {
            <img
              [ngSrc]="activeItem.src"
              [alt]="activeItem.title"
              class="main-image clickable"
              (click)="toggleExpand(true)"
              fill
              priority>
          }
        }
        @if (activeItem.title) {
          <div class="item-caption" aria-hidden="true">
            {{ activeItem.title }}
          </div>
        }
      </div>

      <div class="thumbnail-bar" role="tablist" aria-label="Gallery thumbnails">
        <button class="nav-btn prev" (click)="move(-1)" [disabled]="items.length <= 1" aria-label="Previous item">&lt;</button>
        <div class="thumbnails-wrapper">
          <div class="thumbnails-scroll" [style.transform]="getScrollTransform()">
            @for (item of items; track item.id; let i = $index) {
              <div
                   class="thumb-item"
                   [class.active]="i === activeIndex"
                   (click)="setActive(i)"
                   role="tab"
                   [attr.aria-selected]="i === activeIndex ? 'true' : 'false'"
                   [attr.aria-label]="item.title"
                   tabindex="0"
                   (keydown.enter)="setActive(i)"
                   (keydown.space)="setActive(i); $event.preventDefault()">
                <img [ngSrc]="item.thumb" [alt]="item.title" width="120" height="60">
                @if (item.type === 'youtube') {
                  <div class="play-overlay">
                    <i class="fa fa-play"></i>
                  </div>
                }
              </div>
            }
          </div>
        </div>
        <button class="nav-btn next" (click)="move(1)" [disabled]="items.length <= 1" aria-label="Next item">&gt;</button>
      </div>
    </div>

    <!-- Full Screen Overlay -->
    @if (isExpanded) {
      <div class="full-screen-overlay" (click)="toggleExpand(false)" role="dialog" aria-modal="true">
        <div class="overlay-content" (click)="$event.stopPropagation()">
          <button class="close-btn" (click)="toggleExpand(false)" aria-label="Close full screen">&times;</button>
          @switch (activeItem.type) {
            @case ('youtube') {
              <iframe
                [src]="getSafeUrl(activeItem.src)"
                class="expanded-video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                [title]="activeItem.title">
              </iframe>
            }
            @case ('image') {
              <img
                [ngSrc]="activeItem.src"
                [alt]="activeItem.title"
                class="expanded-image"
                (click)="toggleExpand(false)"
                fill>
            }
          }
          @if (activeItem.title) {
            <div class="expanded-caption">
              {{ activeItem.title }}
            </div>
          }
        </div>
      </div>
    }
  `,
  styles: [`
    .video-carousel-container {
      width: 100%;
      max-width: 960px;
      margin: 0 auto;
      background: #fff;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    }
    .main-display {
      position: relative;
      width: 100%;
      height: 544px;
      background: #f8f9fa;
    }
    .main-display iframe {
      border: 0;
    }
    .main-image {
      object-fit: contain;
    }
    .main-image.clickable {
      cursor: zoom-in;
    }
    .item-caption {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 10px;
      background: rgba(255, 255, 255, 0.85);
      color: #333;
      font-family: Armata, sans-serif, Arial;
      font-size: 14px;
      text-align: right;
      border-top: 1px solid rgba(0,0,0,0.05);
    }
    .thumbnail-bar {
      display: flex;
      align-items: center;
      background: #f8f9fa;
      padding: 10px;
      border-top: 1px solid #dee2e6;
    }
    .thumbnails-wrapper {
      flex: 1;
      overflow: hidden;
      margin: 0 10px;
    }
    .thumbnails-scroll {
      display: flex;
      gap: 8px;
      transition: transform 0.3s ease-out;
    }
    .thumb-item {
      position: relative;
      flex: 0 0 120px;
      height: 60px;
      cursor: pointer;
      opacity: 0.7;
      transition: all 0.2s;
      border: 2px solid transparent;
      border-radius: 4px;
      overflow: hidden;
    }
    .thumb-item.active {
      opacity: 1;
      border-color: #007bff;
    }
    .thumb-item:hover {
      opacity: 1;
    }
    .thumb-item img {
      object-fit: cover;
    }
    .play-overlay {
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0,0,0,0.1);
      color: #fff;
      font-size: 1.5rem;
    }
    .nav-btn {
      background: #e9ecef;
      border: 1px solid #dee2e6;
      color: #495057;
      width: 32px;
      height: 32px;
      cursor: pointer;
      font-weight: bold;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }
    .nav-btn:hover:not(:disabled) {
      background: #dee2e6;
      color: #212529;
    }
    .nav-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .thumb-item:focus {
      outline: 3px solid #007bff;
      opacity: 1;
      z-index: 1;
    }
    .nav-btn:focus {
      outline: 2px solid #007bff;
      z-index: 1;
    }

    @media (max-width: 768px) {
      .main-display { height: 300px; }
      .thumb-item { flex: 0 0 80px; height: 45px; }
    }

    .full-screen-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      cursor: zoom-out;
    }
    .overlay-content {
      position: relative;
      width: 90%;
      height: 90%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    .expanded-image {
      object-fit: contain;
      box-shadow: 0 0 20px rgba(0,0,0,0.5);
    }
    .expanded-video {
      width: 100%;
      height: 100%;
      max-width: 1280px;
      max-height: 720px;
      border: 0;
    }
    .close-btn {
      position: absolute;
      top: -40px;
      right: -20px;
      background: none;
      border: none;
      color: white;
      font-size: 40px;
      cursor: pointer;
      line-height: 1;
      padding: 10px;
    }
    .expanded-caption {
      color: white;
      margin-top: 15px;
      font-size: 18px;
      text-align: center;
      background: rgba(0,0,0,0.5);
      padding: 5px 15px;
      border-radius: 4px;
    }
  `]
})
export class VideoCarouselComponent implements OnInit {
  @Input() items: VideoItem[] = [];
  activeIndex = 0;
  isExpanded = false;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit() {
    if (this.items.length === 0) {
      // Fallback or empty state
    }
  }

  get activeItem(): VideoItem {
    return this.items[this.activeIndex] || { type: 'image', src: '', thumb: '', title: '' };
  }

  setActive(index: number) {
    this.activeIndex = index;
  }

  move(delta: number) {
    this.activeIndex = (this.activeIndex + delta + this.items.length) % this.items.length;
  }

  toggleExpand(expand: boolean) {
    this.isExpanded = expand;
    if (expand) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (this.isExpanded) {
      if (event.key === 'Escape') {
        this.toggleExpand(false);
      } else if (event.key === 'ArrowLeft') {
        this.move(-1);
      } else if (event.key === 'ArrowRight') {
        this.move(1);
      }
    } else {
      if (event.key === 'ArrowLeft') {
        this.move(-1);
      } else if (event.key === 'ArrowRight') {
        this.move(1);
      }
    }
  }

  getSafeUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  getScrollTransform() {
    // Basic centering/scrolling logic
    const thumbWidth = 120 + 8; // width + gap
    const offset = Math.max(0, (this.activeIndex - 2) * thumbWidth);
    return `translateX(-${offset}px)`;
  }
}
